import { LanderCookie } from "./Constants";
import { getCookie } from "./HelperFunctions";

/**
 * Same-origin base path used in "proxy mode". The TLS terminator intercepts
 * this prefix and reverse-proxies it to the real parking API host, rewriting
 * `/api/` back to `/v1/`. Serving the API from the parked domain's own host
 * avoids ad-block / Great-Firewall host-based blocking.
 */
export const PROXY_BASE = "/api";

/**
 * Whether to route the lander API through the same-origin proxy *up front*
 * (before trying the absolute host). Only China traffic qualifies: the Great
 * Firewall blocks the absolute host, so we skip the doomed direct attempt.
 *
 * Everyone else calls the absolute host first (which preserves the
 * `info_cust_idp` login cookie), and `useFetch` falls back to the proxy only
 * if that direct call is actually blocked.
 */
export function shouldProxyUpFront(): boolean {
  return getCookie(LanderCookie.COUNTRY) === "CN";
}

/** True if the URL is already a same-origin proxy path (avoids re-proxying). */
export function isProxyPath(url: string): boolean {
  return url.startsWith(`${PROXY_BASE}/`);
}

/**
 * Convert an absolute parking-API URL to a same-origin proxy path by dropping
 * the scheme+host and swapping the leading `/v1/` for `${PROXY_BASE}/`.
 * e.g. https://api.aws.parking.godaddy.com/v1/parking/landers/x?a=1
 *   -> /api/parking/landers/x?a=1
 * Falls back to the original string if it can't be parsed or isn't a /v1/ URL.
 */
export function toProxyPath(absoluteUrl: string): string {
  try {
    const url = new URL(absoluteUrl);
    if (!url.pathname.startsWith("/v1/")) {
      return absoluteUrl;
    }
    const path = url.pathname.replace(/^\/v1\//, `${PROXY_BASE}/`);
    return `${path}${url.search}`;
  } catch {
    return absoluteUrl;
  }
}
